import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrowOutlined'
import PauseIcon from '@mui/icons-material/PauseCircleOutline'
import ResumeIcon from '@mui/icons-material/PlayCircleOutline'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import EditIcon from '@mui/icons-material/EditOutlined'
import useRouter from '../hooks/useRouter'
import useAccount from '../hooks/useAccount'
import useAlerts, { type UpdateAlertBody } from '../hooks/useAlerts'
import useAlertForm from '../hooks/useAlertForm'
import useAlertSetupSession from '../hooks/useAlertSetupSession'
import useAiAccess from '../hooks/useAiAccess'
import useWhoToAsk from '../hooks/useWhoToAsk'
import { usePipelineNames } from '../hooks/useJobReceipts'
import DeleteConfirmWindow from '../components/system/DeleteConfirmWindow'
import BackLink from '../components/system/BackLink'
import DisabledReason from '../components/system/DisabledReason'
import AlertFormDialog from '../components/alerts/AlertFormDialog'
import { alertConditionLockedReason, alertSetupReason, colleagueAlertReason } from '../utils/alertPermissions'
import { alertSourceOf, eventLabel, isBatchEvent, kindChipLabel } from '../utils/alertSources'
import { describeSchedule } from '../utils/alertSchedule'
import { extractErrorMessage } from '../utils/apitools'
import { AlertRuleStatus, AlertEvaluationResult, AlertEvaluation } from '../types/gotypes'

const STATUS_CHIP_COLOR: Record<AlertRuleStatus, 'success' | 'warning' | 'default'> = {
  active: 'success',
  paused: 'warning',
  archived: 'default',
}

const RESULT_CHIP_COLOR: Record<AlertEvaluationResult, 'error' | 'success' | 'warning'> = {
  running: 'warning',
  triggered: 'error',
  not_triggered: 'success',
  error: 'warning',
}

// Evaluations come back newest-first (ListAlertEvaluations orders by
// created_at DESC — goapi/pkg/store/store_alerts.go:143).
const latestOf = (evaluations: AlertEvaluation[]): AlertEvaluation | undefined => evaluations[0]

const evaluationDetail = (evaluation: AlertEvaluation): string => {
  if (evaluation.result === 'triggered' && evaluation.data?.fire_message) {
    return evaluation.data.fire_message
  }
  if (evaluation.result === 'error') {
    return evaluation.error || 'Error'
  }
  return '-'
}

const stderrTail = (evaluation: AlertEvaluation): string | undefined => {
  const stderr = evaluation.data?.stderr
  return stderr ? stderr : undefined
}

const AlertDetailPage: React.FC = () => {
  const router = useRouter()
  const account = useAccount()
  const [showDelete, setShowDelete] = useState(false)

  const customer = router.params.customer || account.customer?.name || ''
  const alertId = router.params.alertId || ''

  const {
    alert,
    alertIsLoading,
    alertIsError,
    alertError,
    evaluations,
    evaluationsIsLoading,
    updateAlert,
    updateIsLoading,
    deleteAlert,
    deleteIsLoading,
    evaluateAlert,
    evaluateIsLoading,
  } = useAlerts({ customer, alertId })

  const alertForm = useAlertForm(customer)
  const setup = useAlertSetupSession(customer)
  const aiAccess = useAiAccess(customer)
  const whoToAsk = useWhoToAsk(customer)
  const sessionJobs = (account.customer?.sessionJobs || []).filter((j): j is { name: string; displayName?: string } => !!j.name)
  const viewerEmail = account.user?.session_info?.email || ''
  const isManager = !!account.user?.role_permissions?.isManager
  // Only the owner has the agent write the check; a recipient or a
  // colleague who may see the alert looks but does not change it.
  const setupReason = (aiAccess.can('agent_sessions') ? undefined : aiAccess.refusalFor('agent_sessions'))
    ?? alertSetupReason(alert ?? undefined, viewerEmail, whoToAsk)
  const changeReason = colleagueAlertReason(alert ?? undefined, viewerEmail, isManager, whoToAsk)
  const source = alertSourceOf(alert ?? undefined)
  const pipelineId = source.kind === 'event' && isBatchEvent(source.event?.type) ? source.event?.pipeline_id ?? '' : ''
  // Names the pipeline an import-batch event alert is narrowed to; fetches nothing otherwise.
  const pipelineNames = usePipelineNames(customer, pipelineId ? source.job : '')

  // The rule has no tested check (never set up, or its tables, question or
  // condition changed): the agent start screen with its card filled in,
  // where Send opens the chat that writes, tests and saves one.
  const handleWriteCheck = () => {
    if (!alert || setupReason) return
    setup.startSetup(alert)
  }

  const handleChangeWithAgent = () => {
    if (!alert) return
    alertForm.handleClose()
    setup.startSetup(alert)
  }

  const handleSave = async (body: UpdateAlertBody) => {
    if (!alert) return
    try {
      // Never sends status: activating needs a saved check, which a
      // condition change has just cleared.
      await updateAlert(alert.id, body, { quiet: true })
    } catch (err) {
      alertForm.handleError(extractErrorMessage(err))
      return
    }
    alertForm.handleClose()
  }

  const handleOpenSetupConversation = (sessionId: string) => {
    if (!customer) return
    router.navigate('agent-session', { customer, sessionId })
  }

  const handleRunNow = () => {
    if (!alertId || evaluateIsLoading) return
    void evaluateAlert(alertId)
  }

  const handleTogglePause = () => {
    if (!alert || updateIsLoading) return
    const nextStatus: AlertRuleStatus = alert.status === 'active' ? 'paused' : 'active'
    updateAlert(alert.id, { status: nextStatus })
  }

  const handleConfirmDelete = () => {
    if (deleteIsLoading) return
    deleteAlert(alertId)
    setShowDelete(false)
    router.navigate('alerts', { customer })
  }

  const handleCancelDelete = () => {
    if (deleteIsLoading) return
    setShowDelete(false)
  }

  const formatDate = (timestamp: number) => {
    if (!timestamp) return 'Never'
    return new Date(timestamp * 1000).toLocaleString()
  }

  if (alertIsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (alertIsError) {
    const message = alertError instanceof Error ? alertError.message : 'Failed to load alert.'
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{message}</Alert>
      </Box>
    )
  }

  if (!alert) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Alert not found.</Alert>
      </Box>
    )
  }

  const check = alert.config?.check
  const ruleText = alert.config?.natural_language?.rule_text
  const ruleContext = alert.config?.natural_language?.context
  const isEvent = source.kind === 'event'
  const hasSavedScript = Boolean(check?.script_hash)
  // An event alert has no script: it resumes as it was saved, active.
  const canResume = alert.status !== 'active' && (isEvent || hasSavedScript)
  const latest = latestOf(evaluations)
  const latestIsError = latest?.result === 'error'
  const isRunning = evaluations.some((e) => e.result === 'running') || evaluateIsLoading

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: 'calc(100vh - 84px)', p: 3, overflow: 'auto' }}>
      <Box><BackLink label="Back to Alerts" onClick={() => router.navigate('alerts', { customer })} /></Box>
      {/* Header */}
      <Paper elevation={1} sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5">{alert.name}</Typography>
          <Chip
            label={alert.status}
            color={STATUS_CHIP_COLOR[alert.status]}
            size="small"
          />
          <Chip label={kindChipLabel(source.kind)} size="small" variant="outlined" />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <DisabledReason reason={changeReason}>
            <Button
              variant="outlined"
              color="info"
              startIcon={<EditIcon />}
              onClick={() => alertForm.handleOpenEdit(alert)}
            >
              Edit
            </Button>
          </DisabledReason>
          {!isEvent && (changeReason ? (
            <DisabledReason reason={changeReason}>
              <Button variant="outlined" color="info" startIcon={<PlayArrowIcon />}>Run now</Button>
            </DisabledReason>
          ) : (
            <Tooltip title={isRunning ? 'Already running' : hasSavedScript ? 'Run the saved check now' : 'Run now needs a saved check script'}>
              <span>
                <Button
                  variant="outlined"
                  color="info"
                  startIcon={isRunning ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                  onClick={handleRunNow}
                  disabled={isRunning || !hasSavedScript}
                >
                  Run now
                </Button>
              </span>
            </Tooltip>
          ))}
          {changeReason ? (
            <DisabledReason reason={changeReason}>
              <Button variant="outlined" color="info" startIcon={alert.status === 'active' ? <PauseIcon /> : <ResumeIcon />}>
                {alert.status === 'active' ? 'Pause' : 'Resume'}
              </Button>
            </DisabledReason>
          ) : (
            <Tooltip title={alert.status === 'active' ? 'Pause' : canResume ? 'Resume' : 'Resume needs a saved check script'}>
              <span>
                <Button
                  variant="outlined"
                  color="info"
                  startIcon={alert.status === 'active' ? <PauseIcon /> : <ResumeIcon />}
                  onClick={handleTogglePause}
                  disabled={updateIsLoading || (alert.status !== 'active' && !canResume)}
                >
                  {alert.status === 'active' ? 'Pause' : 'Resume'}
                </Button>
              </span>
            </Tooltip>
          )}
          <DisabledReason reason={changeReason}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setShowDelete(true)}
            >
              Delete
            </Button>
          </DisabledReason>
        </Box>
      </Paper>

      {latestIsError && latest && (
        <Alert severity="error">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {latest.error || 'The last check run failed.'}
          </Typography>
          {stderrTail(latest) && (
            <Box
              component="pre"
              sx={{ mt: 1, fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', m: 0 }}
            >
              {stderrTail(latest)}
            </Box>
          )}
        </Alert>
      )}

      {/* Check panel */}
      <Paper elevation={1} sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>{isEvent ? 'Event' : 'Check'}</Typography>
        {source.kind === 'table' && alert.tables?.length > 0 && (
          <Box
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1, mb: 2, bgcolor: 'grey.50', borderRadius: 1 }}
          >
            <Chip label={source.job} size="small" variant="outlined" />
            <Box>
              <Typography variant="body2" color="text.secondary">{alert.tables.length === 1 ? 'Table' : `Tables (${alert.tables.length})`}</Typography>
              {alert.tables.map((t) => (
                <Typography key={`${t.folder_path}/${t.filename}`} variant="body2">
                  {t.display_name || t.filename}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
        {source.kind === 'query' && (
          <Box
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1, mb: 2, bgcolor: 'grey.50', borderRadius: 1 }}
          >
            <Chip label={source.job} size="small" variant="outlined" />
            <Box>
              <Typography variant="body2" color="text.secondary">Question</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{source.query?.question || '—'}</Typography>
            </Box>
          </Box>
        )}
        {isEvent && (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, mb: 2, bgcolor: 'grey.50', borderRadius: 1 }}
          >
            <Chip label={source.job} size="small" variant="outlined" />
            <Typography variant="body2">{eventLabel(source.event?.type)}</Typography>
            {isBatchEvent(source.event?.type) && (
              <Typography variant="body2" color="text.secondary">
                {pipelineId ? `Pipeline: ${pipelineNames[pipelineId] || pipelineId}` : 'Any pipeline'}
              </Typography>
            )}
          </Box>
        )}

        {!isEvent && ruleText && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">Condition</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{ruleText}</Typography>
            {ruleContext && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Context</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{ruleContext}</Typography>
              </>
            )}
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {(alert.recipients?.length ?? 0) > 0
            ? `Notifies ${alert.user} and ${alert.recipients!.join(', ')}`
            : `Notifies ${alert.user}`}
        </Typography>

        {isEvent ? (
          <Typography variant="body2" color="text.secondary">
            No check script and no model: you are notified each time this happens.
          </Typography>
        ) : hasSavedScript && check ? (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {check.summary || 'No summary.'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Schedule: {describeSchedule(alert.config?.schedule)}
            </Typography>
            <Box
              component="pre"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                whiteSpace: 'pre-wrap',
                bgcolor: 'grey.100',
                p: 2,
                borderRadius: 1,
                m: 0,
                maxHeight: 400,
                overflow: 'auto',
              }}
            >
              {check.script}
            </Box>
            {check.setup_session_id && (
              <Box sx={{ mt: 2 }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => handleOpenSetupConversation(check.setup_session_id)}
                >
                  Open setup conversation
                </Link>
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {ruleText ? `The check needs writing for this ${source.kind === 'query' ? 'question' : 'table'} and condition` : 'No check yet'}
            </Typography>
            {alert.config?.schedule && (
              <Typography variant="body2" color="text.secondary">
                Schedule: {describeSchedule(alert.config.schedule)}
              </Typography>
            )}
            <DisabledReason reason={setupReason}>
              <Button
                variant="outlined"
                color="info"
                startIcon={<SmartToyIcon />}
                onClick={handleWriteCheck}
              >
                Write the check with the agent
              </Button>
            </DisabledReason>
            {check?.setup_session_id && (
              <Link
                component="button"
                variant="body2"
                onClick={() => handleOpenSetupConversation(check.setup_session_id)}
              >
                Open setup conversation
              </Link>
            )}
          </Box>
        )}

        <Box sx={{ mt: 2, display: 'flex', gap: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Last evaluated: {formatDate(alert.last_evaluated_at)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last triggered: {formatDate(alert.last_triggered_at)}
          </Typography>
        </Box>
      </Paper>

      {/* Evaluation History */}
      <Paper elevation={1} sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>History</Typography>
        <TableContainer sx={{ flex: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Created</TableCell>
                <TableCell>Trigger</TableCell>
                <TableCell>Result</TableCell>
                <TableCell>Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {evaluationsIsLoading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : evaluations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No evaluations yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                evaluations.map((evaluation) => (
                  <TableRow key={evaluation.id} hover>
                    <TableCell>{formatDate(evaluation.created_at)}</TableCell>
                    <TableCell>{evaluation.trigger_type}</TableCell>
                    <TableCell>
                      <Chip
                        label={evaluation.result.replace('_', ' ')}
                        color={RESULT_CHIP_COLOR[evaluation.result]}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{evaluationDetail(evaluation)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {alertForm.form && (
        <AlertFormDialog
          key={alertForm.formKey}
          open
          alert={alert}
          jobs={sessionJobs}
          job={alertForm.job}
          onJobChange={alertForm.handleJobChange}
          eventType={alertForm.eventType}
          onEventTypeChange={alertForm.handleEventTypeChange}
          pipelines={alertForm.pipelines}
          pipelinesLoading={alertForm.pipelinesLoading}
          recipientOptions={alertForm.recipientOptions}
          recipientsLoading={alertForm.recipientsLoading}
          recipientsError={alertForm.recipientsError}
          recipientQuery={alertForm.recipientQuery}
          onRecipientQueryChange={alertForm.handleRecipientQueryChange}
          lockedReason={alertConditionLockedReason(alert, viewerEmail, whoToAsk)}
          submitting={updateIsLoading}
          error={alertForm.error}
          onSave={handleSave}
          onClose={alertForm.handleClose}
          onChangeWithAgent={handleChangeWithAgent}
        />
      )}

      <DeleteConfirmWindow
        open={showDelete}
        title="Delete Alert"
        message={`Are you sure you want to delete "${alert.name}"? This action cannot be undone.`}
        confirmWord="delete"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </Box>
  )
}

export default AlertDetailPage
